import {
  getAuthControllerLoginPinUrl,
  getPosAuthControllerRefreshUrl,
  getPosPushNotificationControllerGetPushSubscriptionsUrl,
  getPosPushNotificationControllerSubscribePushUrl,
  getPosPushNotificationControllerUnsubscribePushUrl,
  getPosPushNotificationListCustomControllerFindCustomListsUrl,
  getPosPushNotificationListHandlingItemControllerFindHandlingItemListsUrl,
  getPosPushNotificationListOfferControllerFindOfferListsUrl,
  HandlingItemStatus,
  UserRole,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';

/**
 * Endpoints whose error statuses are part of their normal flow - a wrong PIN, a
 * device token that quietly expired between two users - and are always
 * handled by the caller. The global response interceptors skip them so an
 * expected 401/403 never becomes a toast, a token refresh or a bounce to the
 * forbidden page.
 *
 * Everything POS-token authorized belongs here: those calls carry the device's
 * bearer, not the app session, so refreshing the *user* token on their 401
 * would rotate the wrong credential - and a 403 means the device has to be
 * checked back in, which is not something the forbidden page tells anyone.
 * `usePosRequest` replays them behind a device refresh instead.
 */
const SELF_HANDLED_ERROR_PATHS: string[] = [
  getAuthControllerLoginPinUrl(),
  getPosAuthControllerRefreshUrl(),
  getPosPushNotificationControllerSubscribePushUrl(),
  getPosPushNotificationControllerUnsubscribePushUrl(),
  getPosPushNotificationControllerGetPushSubscriptionsUrl(),
  getPosPushNotificationListOfferControllerFindOfferListsUrl(),
  getPosPushNotificationListCustomControllerFindCustomListsUrl(),
  getPosPushNotificationListHandlingItemControllerFindHandlingItemListsUrl(),
];

/** @param url Absolute request URL as the SDK's response interceptors receive it. */
export const isSelfHandledErrorUrl = (url: string) =>
  SELF_HANDLED_ERROR_PATHS.some((path) => url.endsWith(path));

/**
 * Statuses a handling item may still be corrected in. Everything past that is
 * either being processed or already done, so the review edit has no effect.
 */
export const EDITABLE_HANDLING_ITEM_STATUSES: HandlingItemStatus[] = [
  HandlingItemStatus.PENDING,
  HandlingItemStatus.UNDER_REVIEW,
];

export const OFFER_REVIEW_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.OFFER_REVIEW];

export const HANDLING_REVIEW_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.HANDLING_REVIEW];

export const ADMIN_ROLES: UserRole[] = [UserRole.ADMIN];

export const HANDLING_ROLES: UserRole[] = [UserRole.ITEM_HANDLING, UserRole.ADMIN];

export const RESTRICTED_ROLES: UserRole[] = [
  UserRole.USER,
  UserRole.READONLY,
  UserRole.EXTERNAL_USER,
];

export const BETA_TESTER_ROLES: UserRole[] = [UserRole.BETA_TESTER];

/** Roles allowed to check a device into POS mode - and back out of it. */
export const STORE_MANAGER_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.STORE_MANAGER];

/**
 * Roles a store manager may add to and remove from a user. Everything outside
 * this list can neither be assigned nor revoked by them.
 * Follows `GlobalUserManagementConfig.storeManagerEditRoles` on the server, but
 * is deliberately stricter: BETA_TESTER stays locked here even though the server
 * would accept it.
 */
export const STORE_MANAGER_EDIT_ROLES: UserRole[] = [
  UserRole.ITEM_HANDLING,
  UserRole.STORE_MANAGER,
  UserRole.USER,
];

/**
 * Roles that lock a user for store managers: while a user holds one of these,
 * a store manager cannot change anything about that user.
 * Mirrors `GlobalUserManagementConfig.storeManagerLockRoles` on the server.
 */
export const STORE_MANAGER_LOCK_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.OFFER_REVIEW,
  UserRole.HANDLING_REVIEW,
];

/**
 * PIN length range for this client. The backend accepts 4-8, but the login
 * keypad renders PIN_MAX_LENGTH cells, so setting a PIN longer than that would
 * make it unenterable - keep both screens on these constants.
 */
export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;

/**
 * Inactivity after which a half-entered PIN is wiped from the pad, so digits
 * left behind by whoever walked away are not waiting for the next person.
 */
export const PIN_ENTRY_RESET_TIMEOUT = 30 * 1000;

/** Mirrors the `IsStrongPassword` defaults the server validates against. */
export const PASSWORD_MIN_LENGTH = 8;

/** Mirrors the `varchar(4)` column behind `user.abbreviation` on the server. */
export const ABBREVIATION_MAX_LENGTH = 4;

/**
 * Barcode patterns the global scan handler listens for on every screen. They
 * carry the id the target route needs in their first capture group, so a match
 * is enough to navigate.
 */
export const OFFER_RECEIPT_SCAN_PATTERN = /^A(\d+)R(\d+)$/;
export const HANDLING_ITEM_SCAN_PATTERN = /^KA0*(\d+)$/;

export const READONLY_ROLES: UserRole[] = [
  UserRole.READONLY,
  UserRole.EXTERNAL_INTEGRATION_READONLY,
];

export const EXTERNAL_ROLES: UserRole[] = [
  UserRole.EXTERNAL_INTEGRATION,
  UserRole.EXTERNAL_USER,
  UserRole.EXTERNAL_INTEGRATION_READONLY,
];
